/**
 * P1 운영 관제 센터.
 *
 * "오늘 시스템이 정상인가?"를 5초 안에 답할 수 있는 화면.
 * - 시스템 상태 9칸 카드 (Trading / Order / Market / Scheduler / Broker / Backup / Trade·Daily·Ops Alerts)
 * - 24h 활동 요약 + 최근 거부/이슈 이벤트
 * - 30초 폴링
 */
import { useEffect, useState, useCallback } from 'react';
import OperationsStatusCards from '../components/operations/OperationsStatusCards';
import OperationsActivity from '../components/operations/OperationsActivity';
import OperationsLivePilotGuards from '../components/operations/OperationsLivePilotGuards';
import OperationsWatchlist from '../components/operations/OperationsWatchlist';
import OperationsAnalystShadow from '../components/operations/OperationsAnalystShadow';
import CrossMarketSnapshot from '../components/market/CrossMarketSnapshot';
import MarketScopeTabs from '../components/market/MarketScopeTabs';
import { useCrossMarketStatus } from '../hooks/useCrossMarketStatus';
import {
  fetchOperationsOverview,
  OperationsOverview,
} from '../services/operationsApi';
import type { MarketScope } from '../types/marketScope';
import './Operations.css';

const POLL_MS = 30_000;

export default function Operations() {
  const [data, setData] = useState<OperationsOverview | null>(null);
  const [scope, setScope] = useState<MarketScope>('ALL');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const {
    data: marketData,
    loading: marketLoading,
    error: marketError,
    reload: reloadMarketStatus,
  } = useCrossMarketStatus();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const overview = await fetchOperationsOverview(24, 5);
      setData(overview);
      setRefreshedAt(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : '운영 관제 조회 실패');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshAll = useCallback(() => {
    load();
    reloadMarketStatus();
  }, [load, reloadMarketStatus]);

  useEffect(() => {
    load();
    const interval = setInterval(load, POLL_MS);
    return () => clearInterval(interval);
  }, [load]);

  if (loading && !data) return <InitialState text="운영 상태 로딩 중..." />;
  if (error && !data) return <InitialState text={`API 오류: ${error}`} onRetry={refreshAll} error />;

  if (!data) return null;

  return (
    <div className="operations-page">
      <OperationsHeader
        refreshedAt={refreshedAt} loading={loading || marketLoading}
        onRefresh={refreshAll}
      />
      {error && <div className="state-box error-state op-banner">최신 데이터 조회 실패: {error} (이전 데이터 표시 중)</div>}
      <MarketScopeTabs value={scope} onChange={setScope} status={marketData} />
      <CrossMarketSnapshot status={marketData} scope={scope} loading={marketLoading} error={marketError} />
      <OperationsMarketBody scope={scope} data={data} onAnalystUpdated={load} />
    </div>
  );
}

function InitialState({ text, onRetry, error = false }: InitialStateProps) {
  return (
    <div className="operations-page">
      <div className={`state-box${error ? ' error-state' : ''}`}>{text}</div>
      {onRetry && <button className="op-refresh" onClick={onRetry}>다시 시도</button>}
    </div>
  );
}

function OperationsHeader({ refreshedAt, loading, onRefresh }: HeaderProps) {
  const refreshed = refreshedAt ? `${refreshedAt.toLocaleTimeString('ko-KR')} 갱신` : '갱신 대기';
  return (
    <header className="op-header">
      <div><h1>운영 관제</h1><p>오늘 시스템이 정상인가? · 30초 자동 새로고침 · {refreshed}</p></div>
      <button className="op-refresh" onClick={onRefresh} disabled={loading}>
        {loading ? '갱신 중...' : '새로고침'}
      </button>
    </header>
  );
}

function OperationsMarketBody({ scope, data, onAnalystUpdated }: BodyProps) {
  if (scope === 'KR') return <KrOperationsReadonly />;
  return (
    <>
      <OperationsStatusCards data={data} />
      <OperationsLivePilotGuards status={data.live_pilot_guards} />
      <OperationsWatchlist analysis={data.watchlist_analysis} />
      <OperationsAnalystShadow summary={data.analyst_shadow} onUpdated={onAnalystUpdated} />
      <OperationsActivity data={data} />
    </>
  );
}

function KrOperationsReadonly() {
  return (
    <section className="op-kr-readonly state-box">
      <strong>KR 운영 관제는 검증 단계입니다.</strong>
      <p>Toss 실주문, KR broker sync, KR scheduler, KR 실체결 알림은 아직 활성화하지 않았습니다.</p>
    </section>
  );
}

type InitialStateProps = { text: string; onRetry?: () => void; error?: boolean };
type HeaderProps = { refreshedAt: Date | null; loading: boolean; onRefresh: () => void };
type BodyProps = { scope: MarketScope; data: OperationsOverview; onAnalystUpdated: () => void };
